import React from 'react';
import styled from 'styled-components/macro';
import { useHistory } from 'react-router';
import { removeCustomReport, useCustomReportsContext } from 'lib/context';
import { ModalDelete } from 'lib/components';
import ClockIcon from 'lib/images/ClockIcon';
import DeleteIcon from 'lib/images/DeleteIcon';
import { reportData } from 'lib/const';
import { getRecurringTitle } from '.';
import { Heading } from 'lib/components/styles/typography';
import { Gap, HeaderWrapper } from 'lib/components/styles/layout';
import { VIDEO_ATTRIBUTES } from 'lib/utils/videoAttributes';
import { CustomReportItem as CustomReportItemType } from 'lib/context/types';
import { Button } from 'react-covideo-common';
import { MdEdit } from 'react-icons/md';

const Container = styled.div`
  margin-top: 16px;
  padding: 24px;
  border-radius: 4px;
  box-shadow: 0 4px 12px 0 #1d1e240a;
  border: solid 1px #f7f8fa;
  align-self: flex-start;
`;

const Users = styled.div`
  padding: 8px 0;
`;

const Reports = styled.div`
  display: flex;
  flex-wrap: wrap;
  row-gap: 6px;
  & > div {
    padding: 4px 8px; /*** SUS-781 changes ***/
    margin-right: 6px;
    border-radius: 3px;
    background-color: rgba(0, 27, 83, 0.05);
    height: 24px;

    font-size: 15px;
    font-weight: normal;
    font-stretch: normal;
    font-style: normal;
    line-height: 1.5;
    letter-spacing: normal;
    color: #272a32;
  }
`;

const Recurring = styled.div`
  font-size: 16px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.5;
  letter-spacing: normal;
  color: #272a32;
  margin-top: 24px;
`;

const RecurringItem = styled.div`
  display: flex;
  margin-top: 16px;
  align-items: center;
  & > svg {
    margin-right: 8px;
  }
`;

const EmailsContainer = styled.div`
  margin-left: 32px;
`;

const Email = styled.div`
  font-size: 14px;
  font-weight: normal;
  font-stretch: normal;
  font-style: normal;
  line-height: 1.71;
  letter-spacing: normal;
  color: #4e5461;
  margin-right: 12px;
`;

type Props = {
  report: CustomReportItemType;
  setDeleteReport: React.Dispatch<
    React.SetStateAction<boolean>
  > /*** SUS-781 changes ***/;
};

export const CustomReportItem = (props: Props) => {
  const [showDeleteModal, setShowDeleteModal] = React.useState(false);

  const { report, setDeleteReport } = props; /*** SUS-781 changes ***/
  const { dispatch } = useCustomReportsContext();
  const history = useHistory();
  let usersLabel = '';
  if (!report?.usersData || !report.usersData.length) {
    usersLabel = '0 Users'; // should never be the case
  } else if (report.isForAllUsers) {
    usersLabel = `All ${report.usersData.length} User${
      report.usersData.length !== 1 ? 's' : ''
    }`;
  } else if (report.usersData.length > 5) {
    usersLabel = `${report.usersData.length} Users`;
  } else {
    usersLabel = (report?.usersData || [])
      .map(userData => `${userData.firstName || ''} ${userData.lastName || ''}`)
      .join(', ');
  }

  return (
    <Container>
      <HeaderWrapper>
        <Heading>{report.title}</Heading>
        <Gap gap='8px'>
          <Button
            text='View Details'
            icon={<MdEdit width={20} />}
            onClick={() => history.push('/reports/custom/' + report.reportId)}
          />
          <Button
            icon={<DeleteIcon />}
            variant='destructive'
            onClick={() => setShowDeleteModal(true)}
          />
        </Gap>
      </HeaderWrapper>
      {report.users && <Users>Users: {usersLabel}</Users>}

      <Reports>
        {(report.reports || []).map((tag: string) => {
          const report =
            reportData[tag.includes(VIDEO_ATTRIBUTES) ? VIDEO_ATTRIBUTES : tag];
          let label = tag;
          if (report) {
            label = report.label;
          }
          return <div key={tag}>{label}</div>;
        })}
      </Reports>
      {!!(report.recurrings || []).length && (
        <Recurring>
          <div>Recurring</div>
          {(report?.recurrings || []).map((recurring: any) => (
            <div key={recurring.recurringId}>
              <RecurringItem>
                <ClockIcon color='#d0d3d9' />
                {getRecurringTitle(recurring)}
              </RecurringItem>
              <EmailsContainer>
                {recurring.recipients.map((email: string) => (
                  <Email key={email}>{email}</Email>
                ))}
              </EmailsContainer>
            </div>
          ))}
        </Recurring>
      )}
      {showDeleteModal && (
        <ModalDelete
          whiteButtonText='Yes, Delete'
          orangeButtonText='No, Cancel'
          title='Delete custom report?'
          text={`This action can’t be undone.`}
          handleModalClose={() => setShowDeleteModal(false)}
          onClickWhiteButton={async () => {
            setShowDeleteModal(false);
            await removeCustomReport({
              dispatch,
              data: {
                reportId: report.reportId,
              },
            });
            setDeleteReport(true); /*** SUS-781 changes ***/
          }}
          onClickOrangeButton={() => setShowDeleteModal(false)}
        />
      )}
    </Container>
  );
};
